import {
  closestCenter,
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { Button } from "@volca/ui/components/ui/button";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import { SettingsRow } from "./settings-group";
import { SortableRule } from "./sortable-rule";
import type { useRuleOrder } from "./use-rule-order";
import type { TemplateSettingsState } from "./use-settings-state";

export const ReorderableRules = ({
  reportId,
  runId,
  state,
  order,
  search,
  onClearSearch,
}: {
  reportId: string;
  runId: string | null;
  state: TemplateSettingsState;
  order: ReturnType<typeof useRuleOrder>;
  search: string;
  onClearSearch: () => void;
}) => {
  const { settings, editable, save } = state;
  const rules = settings?.rules ?? [];
  const shown = rules.filter((rule) =>
    `${rule.label} ${rule.checks}`
      .toLowerCase()
      .includes(search.trim().toLowerCase()),
  );
  const { saveOrder, busy, message } = order;
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
  );
  const canReorder = editable && !busy && !search.trim() && rules.length > 1;
  const label = (id: string | number) =>
    rules.find((rule) => rule.id === id)?.label ?? "Rule";
  const move = async (id: string, to: number) => {
    if (!canReorder) return;
    const ids = rules.map((rule) => rule.id);
    await saveOrder(arrayMove(ids, ids.indexOf(id), to));
  };
  return (
    <>
      {editable && search.trim() && rules.length > 1 && (
        <Text variant="caption" tone="muted" className="px-4 py-2">
          Clear search to reorder.
        </Text>
      )}
      <Text role="status" aria-live="polite" className="sr-only">
        {message}
      </Text>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={({ active, over }) => {
          if (over && active.id !== over.id)
            move(
              String(active.id),
              rules.findIndex((rule) => rule.id === over.id),
            );
        }}
        accessibility={{
          screenReaderInstructions: {
            draggable: "Drag to reorder, or press Enter to open move options.",
          },
          announcements: {
            onDragStart: ({ active }) => `Moving ${label(active.id)}.`,
            onDragOver: ({ active, over }) =>
              over
                ? `${label(active.id)}, position ${rules.findIndex((rule) => rule.id === over.id) + 1} of ${rules.length}.`
                : undefined,
            onDragEnd: ({ active, over }) =>
              over
                ? `${label(active.id)} dropped at position ${rules.findIndex((rule) => rule.id === over.id) + 1}.`
                : "Reorder cancelled.",
            onDragCancel: () => "Reorder cancelled. Order unchanged.",
          },
        }}
      >
        <SortableContext
          items={rules.map((rule) => rule.id)}
          strategy={verticalListSortingStrategy}
        >
          <Stack
            render={<ul aria-label="Review rules" />}
            className="divide-y divide-line"
          >
            {shown.map((rule) => (
              <SortableRule
                key={rule.id}
                rule={rule}
                reportId={reportId}
                runId={runId}
                editable={editable}
                busy={busy}
                canReorder={canReorder}
                index={rules.findIndex((item) => item.id === rule.id)}
                count={rules.length}
                onMove={(to) => move(rule.id, to)}
                onToggle={(enabled) => {
                  void save({ rules: { [rule.id]: { enabled } } }).catch(
                    () => undefined,
                  );
                }}
              />
            ))}
          </Stack>
        </SortableContext>
      </DndContext>
      {!shown.length && rules.length > 0 && (
        <SettingsRow
          label="No matching rules"
          action={
            <Button variant="ghost" size="sm" onClick={onClearSearch}>
              Clear search
            </Button>
          }
        />
      )}
    </>
  );
};
